// web/src/screens/admin/cards_edit.ts — Plan 11-06 Task 2.
//
// Card edit form + Tasks panel. Resolves the parent Game via I-07
// by-short-code resolver, then fetches cards scoped to the game and
// finds the active card by short_code (per I-07b — Cards/Tasks
// short_codes are scoped, not globally resolvable).
//
// All API-supplied text via textContent / input.value (T-11-09a).

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import { adminGet, adminPatch, AdminError, adminDelete } from "../../api/admin.js";
import { renderTasksPanel } from "./tasks_list.js";
import {
  USER_PROMPT_VARS, promptVarsInfoBox,
  mountOverridePromptField, type OverridePromptHandle,
} from "./prompt_vars.js";
import type { AdminGameDTO, AdminCardDTO, AdminTaskDTO, AdminRouteProps } from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function field(parent: HTMLElement, label: string, input: HTMLElement): void {
  const wrap = document.createElement("label");
  wrap.setAttribute("style", "display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151;");
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.setAttribute("style", "font-weight: 500;");
  wrap.appendChild(lab);
  wrap.appendChild(input);
  parent.appendChild(wrap);
}

function makeInput(type: string): HTMLInputElement {
  const el = document.createElement("input");
  el.type = type;
  el.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px;");
  return el;
}

function emptyToNull(v: string): string | null {
  return v.trim() === "" ? null : v;
}

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = props as AdminRouteProps | undefined;
  const gc = p && p.kind === "admin-card-edit" ? p.gameShortCode : "";
  const cc = p && p.kind === "admin-card-edit" ? p.cardShortCode : "";

  const main = renderAdminShell(root, [
    { label: "Games", href: "/admin/games" },
    { label: `Game: ${gc || "?"}`, href: `/admin/games/${encodeURIComponent(gc)}` },
    { label: `Card: ${cc || "?"}` },
  ]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const form = document.createElement("form");
  form.setAttribute("style", "display: none; flex-direction: column; gap: 12px; max-width: 640px; background: white; padding: 16px; border-radius: 6px; border: 1px solid #e5e7eb;");
  main.appendChild(form);

  const errorBox = document.createElement("div");
  errorBox.setAttribute("style", "margin-top: 12px; padding: 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; display: none;");
  main.appendChild(errorBox);

  const tasksContainer = document.createElement("section");
  tasksContainer.setAttribute("style", "margin-top: 24px; max-width: 720px;");
  main.appendChild(tasksContainer);

  const initialTaskSelect = document.createElement("select");
  initialTaskSelect.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: white;");

  const refs = {
    short_code: makeInput("text"),
    ordinal: makeInput("number"),
    initial_task_short_code: initialTaskSelect,
  };
  refs.short_code.readOnly = true;
  refs.short_code.title = "short_code is immutable";
  refs.short_code.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: #f3f4f6; color: #6b7280;");
  field(form, "Short code", refs.short_code);
  field(form, "Ordinal", refs.ordinal);
  field(form, "Initial task", refs.initial_task_short_code);

  function setInitialTaskOptions(tasks: AdminTaskDTO[], selected: string): void {
    initialTaskSelect.replaceChildren();
    const none = document.createElement("option");
    none.value = "";
    none.textContent = "(none)";
    initialTaskSelect.appendChild(none);
    let matched = selected === "";
    for (const t of tasks) {
      const sc = t.short_code;
      if (!sc) continue;
      const opt = document.createElement("option");
      opt.value = sc;
      opt.textContent = sc;
      initialTaskSelect.appendChild(opt);
      if (sc === selected) matched = true;
    }
    if (!matched && selected !== "") {
      const stale = document.createElement("option");
      stale.value = selected;
      stale.textContent = `${selected} (missing)`;
      initialTaskSelect.appendChild(stale);
    }
    initialTaskSelect.value = selected;
  }

  // migration 0034: per-card user-prompt override. Off by default →
  // inherit from the parent game (or, if the game has no override, the
  // Settings default).
  const llmUserPromptW: OverridePromptHandle = mountOverridePromptField(
    "Override LLM user prompt (inherit from Game/Settings when off)",
    promptVarsInfoBox(USER_PROMPT_VARS, "User-prompt substitution variables:"),
    6,
  );
  form.appendChild(llmUserPromptW.container);

  const buttonRow = document.createElement("div");
  buttonRow.setAttribute("style", "display: flex; gap: 8px; margin-top: 8px;");
  const saveBtn = document.createElement("button");
  saveBtn.type = "submit";
  saveBtn.textContent = "Save";
  saveBtn.setAttribute("style", "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.textContent = "Delete";
  deleteBtn.setAttribute("style", "padding: 8px 14px; background: #dc2626; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px; margin-left: auto;");
  buttonRow.append(saveBtn, deleteBtn);
  form.appendChild(buttonRow);

  let game: AdminGameDTO | null = null;
  let card: AdminCardDTO | null = null;

  function fillForm(c: AdminCardDTO): void {
    refs.short_code.value = c.short_code ?? "";
    refs.ordinal.value = c.ordinal == null ? "" : String(c.ordinal);
    refs.initial_task_short_code.value = c.initial_task_short_code ?? "";
    llmUserPromptW.setValue(c.llm_user_prompt);
  }

  async function loadInitialTaskOptions(cardID: string, selected: string): Promise<void> {
    try {
      const tasks = await adminGet<AdminTaskDTO[]>(
        `/api/admin/cards/${encodeURIComponent(cardID)}/tasks`,
      );
      setInitialTaskOptions(tasks, selected);
    } catch {
      setInitialTaskOptions([], selected);
    }
  }

  async function load(): Promise<void> {
    try {
      const g = await adminGet<AdminGameDTO>(
        `/api/admin/games/by-short-code/${encodeURIComponent(gc)}`,
      );
      game = g;
      if (!g.id) throw new Error("game has no id");
      const cards = await adminGet<AdminCardDTO[]>(
        `/api/admin/games/${encodeURIComponent(g.id)}/cards`,
      );
      const found = cards.find((x) => x.short_code === cc);
      if (!found) {
        status.textContent = `Card “${cc}” not found in game “${gc}”.`;
        return;
      }
      card = found;
      fillForm(found);
      if (found.id) await loadInitialTaskOptions(found.id, found.initial_task_short_code ?? "");
      status.style.display = "none";
      form.style.display = "flex";

      tasksContainer.replaceChildren();
      const h = document.createElement("h2");
      h.textContent = "Tasks";
      h.setAttribute("style", "font-size: 16px; margin: 0 0 8px 0; color: #111827;");
      tasksContainer.appendChild(h);
      if (found.id) renderTasksPanel(tasksContainer, found.id, gc, cc);
    } catch (err) {
      if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
      } else {
        status.textContent = `Failed to load: ${(err as Error).message}`;
      }
    }
  }

  async function onSave(e: Event): Promise<void> {
    e.preventDefault();
    if (!card || !card.id) return;
    const patch: Partial<AdminCardDTO> = {};
    if (refs.short_code.value !== (card.short_code ?? "")) {
      patch.short_code = refs.short_code.value;
    }
    const ordNew = refs.ordinal.value === "" ? null : Number(refs.ordinal.value);
    if ((card.ordinal ?? null) !== ordNew) {
      patch.ordinal = ordNew;
    }
    if ((card.initial_task_short_code ?? "") !== refs.initial_task_short_code.value) {
      patch.initial_task_short_code = emptyToNull(refs.initial_task_short_code.value);
    }
    // migration 0034: empty string sent verbatim → server clears NULL (inherit).
    const userPromptOverride = llmUserPromptW.value();
    if ((card.llm_user_prompt ?? "") !== userPromptOverride) {
      patch.llm_user_prompt = userPromptOverride;
    }
    if (Object.keys(patch).length === 0) {
      showAdminToast(document.body, "No changes to save");
      return;
    }
    try {
      const cardsURL = `/api/admin/cards/${encodeURIComponent(card.id)}`;
      const updated = await adminPatch<Partial<AdminCardDTO>, AdminCardDTO>(cardsURL, patch);
      card = updated;
      fillForm(updated);
      showAdminToast(document.body, "Saved");
      errorBox.style.display = "none";
      if (updated.short_code && updated.short_code !== cc) {
        history.replaceState({}, "", `/admin/games/${encodeURIComponent(gc)}/cards/${encodeURIComponent(updated.short_code)}`);
      }
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Save failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Save failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onDelete(): Promise<void> {
    if (!card || !card.id || !game) return;
    if (!window.confirm(`Delete card ${card.short_code}? This cannot be undone.`)) return;
    try {
      await adminDelete(`/api/admin/cards/${encodeURIComponent(card.id)}`);
      showAdminToast(document.body, "Deleted");
      navigateAdmin(`/admin/games/${encodeURIComponent(gc)}`);
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Delete failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Delete failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  form.addEventListener("submit", onSave);
  deleteBtn.addEventListener("click", onDelete);

  void load();

  return () => {
    form.removeEventListener("submit", onSave);
    deleteBtn.removeEventListener("click", onDelete);
  };
}
